import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { CreditCard, ArrowRight, Key } from 'lucide-react';
import { i18n } from '../utils/i18n';
import { getBrandName } from '../utils/brand';

interface LoginProps {
  onLogin: (nickname: string, pin: string) => Promise<void>;
  onSwitchToRegister: () => void;
  isLoading: boolean;
}

export function Login({ onLogin, onSwitchToRegister, isLoading }: LoginProps) {
  const [nickname, setNickname] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(true);

  // 비밀번호 초기화
  const [showReset, setShowReset] = useState(false);
  const [rNick, setRNick] = useState('');
  const [rPhone, setRPhone] = useState('');
  const [rPin, setRPin] = useState('');
  const [rPin2, setRPin2] = useState('');
  const [rBusy, setRBusy] = useState(false);

  const submitReset = async () => {
    if (!rNick.trim() || !rPhone.trim() || !rPin.trim()) {
      toast.error('아이디, 휴대폰번호, 새 비밀번호를 모두 입력하세요');
      return;
    }
    if (!/^\d{4,6}$/.test(rPin.trim())) {
      toast.error('비밀번호는 숫자 4~6자리여야 합니다');
      return;
    }
    if (rPin !== rPin2) {
      toast.error('새 비밀번호가 서로 다릅니다');
      return;
    }
    setRBusy(true);
    try {
      const res = await fetch('/api/auth/reset-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: rNick.trim(), phone: rPhone.trim(), newPin: rPin.trim() }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || '변경에 실패했습니다');
      toast.success('비밀번호가 변경되었습니다');
      // 바로 로그인할 수 있게 아이디를 채워준다
      setNickname(rNick.trim());
      setPin('');
      setShowReset(false);
      setRNick(''); setRPhone(''); setRPin(''); setRPin2('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : '변경에 실패했습니다');
    }
    setRBusy(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nickname.trim() || !pin.trim()) {
      toast.error(i18n.t('pleaseEnterBoth'));
      return;
    }

    try {
      await onLogin(nickname, pin);
      toast.success(i18n.t('welcomeBack'));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : i18n.t('loginFailed'));
    }
  };

  return (
    <div className="h-full bg-background flex flex-col justify-center p-6">
      {/* Logo */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl mb-4 shadow-lg" style={{border: '3px solid #FF6B00'}}>
          <img src="/handy-logo.svg" alt="Handy" width="55" height="55" />
        </div>
        <h1 className="text-4xl font-light text-foreground tracking-wide" style={{color: '#FF6B00'}}>Handy</h1>
        <p className="text-muted-foreground text-base mt-1">Handy Blockchain Wallet</p>
      </div>

      {/* Login Form */}
      <div className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="text"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder={i18n.t('usernamePlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={2}
              maxLength={10}
            />
          </div>
          
          <div>
            <Input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder={i18n.t('pinPlaceholder')}
              className="h-12 bg-input-background border-border text-foreground placeholder:text-muted-foreground text-base"
              disabled={isLoading}
              minLength={6}
              maxLength={10}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 mt-8 text-white text-base font-medium rounded-xl flex items-center justify-center gap-3" style={{background: "#FF6B00"}}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <span>{i18n.t('signIn')}</span>
                <ArrowRight size={20} />
              </>
            )}
          </Button>
        </form>

        {/* 비밀번호를 잊은 사람은 로그인을 못 하니 설정 화면의 PIN 변경을 쓸 수 없다.
            여기서 아이디 + 가입 시 휴대폰번호로 확인해 바로 바꾼다. */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => { setShowReset(true); setRNick(nickname); }}
            className="text-sm underline"
            style={{ color: '#FF6B00' }}
            disabled={isLoading}
          >
            비밀번호를 잊으셨나요?
          </button>
        </div>

        {/* Register Link */}
        <div className="text-center pt-2">
          <p className="text-muted-foreground text-sm mb-4">{i18n.t('dontHaveAccount')}</p>
          <Button 
            onClick={onSwitchToRegister}
            variant="outline"
            className="w-full h-12 border-border text-foreground bg-transparent hover:bg-muted text-base"
            disabled={isLoading}
          >
            {i18n.t('createAccount')}
          </Button>
        </div>
      </div>

      {showReset && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
             onClick={() => !rBusy && setShowReset(false)}>
          {/* 설정 화면의 PIN 변경 팝업과 같은 형태로 맞춘다.
              앱 안에서 팝업 생김새가 제각각이면 같은 앱처럼 안 보인다. */}
          <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-2xl"
               style={{ maxHeight: '88vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Key className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">비밀번호 재설정</h3>
              <p className="text-muted-foreground text-sm">가입할 때 입력한 휴대폰번호로 확인합니다</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  아이디
                </label>
                <input
                  value={rNick}
                  onChange={e => setRNick(e.target.value)}
                  placeholder="아이디"
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  휴대폰번호
                </label>
                <input
                  value={rPhone}
                  onChange={e => setRPhone(e.target.value)}
                  inputMode="tel"
                  placeholder="01012345678"
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  새 비밀번호
                </label>
                <input
                  type="password"
                  value={rPin}
                  onChange={e => setRPin(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="숫자 4~6자리"
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  새 비밀번호 확인
                </label>
                <input
                  type="password"
                  value={rPin2}
                  onChange={e => setRPin2(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="한 번 더 입력"
                  className="w-full px-4 py-3 border border-border rounded-xl bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  onKeyPress={e => { if (e.key === 'Enter') submitReset(); }}
                />
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={() => setShowReset(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={rBusy}
                >
                  취소
                </Button>
                <Button
                  onClick={submitReset}
                  disabled={rBusy || !rNick || !rPhone || !rPin || !rPin2}
                  className="flex-1 text-white"
                  style={{ background: '#FF6B00' }}
                >
                  {rBusy ? '변경 중…' : '비밀번호 변경'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}